/**
 * Route guard — the SPA half of what `middleware.ts` + `lib/access/guard.ts` did.
 *
 * `<RequireService service="wbr">` gates an area on the signed-in user having
 * that product; `<RequireService admin>` gates the platform-admin area. Anyone
 * not signed in is bounced to /login, and a signed-in user without the service
 * lands back on /dashboard — the same redirects the server guard performed.
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { ServiceId } from "@bootwhat/shared/access";
import { useSession } from "@/lib/session";

interface Props {
  service?: ServiceId;
  admin?: boolean;
}

export default function RequireService({ service, admin = false }: Props) {
  const { loading, profile } = useSession();
  const location = useLocation();

  // Nothing is decided until /api/auth/me answers — rendering the redirect early
  // would bounce a signed-in user to /login on every hard refresh.
  if (loading) return <div className="wbr-boot" aria-busy="true" />;

  if (!profile) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  // Admins implicitly hold every service (getAccessProfile does the same).
  const permitted = admin ? profile.isAdmin : profile.isAdmin || profile.allowedServices.includes(service!);
  if (!permitted) return <Navigate to="/dashboard" replace />;

  return <Outlet />;
}
